import './review.scss';
import { MooringOfferService, MooringService } from 'services/mooring';
import { autoinject, bindable, Container } from 'aurelia-framework';
import { AsyncErrorHandler } from 'lib/ui';
import { MooringReviewDto, CommentForEntity, mooringReviewStatusToText } from 'models/mooring';
import { PubSub } from 'lib/event/PubSub';
import { ConfirmDialog } from 'components/dialogs/confirm/confirm-dialog';
import { ToastService } from 'services';
import { sortDateDesc } from 'lib/helpers/sorting';
import { OfferViewContext } from '../../OfferViewContext';

type ComponentComment = {
  Entity: CommentForEntity;
  EntityId: number;

  Comment: string;
  Resolved: boolean;
};

@autoinject()
export class Review {
  @bindable()
  protected mooringId: number;

  @bindable
  protected offerId: number;

  @bindable()
  protected reviewId?: number;

  reviewIdChanged(n?: number) {
    void this.getReiew(n);
    void this.getReviews();
  }

  protected context = OfferViewContext.useContext(this.container);

  // Current or last review for the mooring
  protected review?: MooringReviewDto;

  // All reviews for the mooring
  protected reviews: MooringReviewDto[] = [];

  protected anchorLines: ComponentComment[] = [];
  protected anchorLinesResolved: number = 0;

  protected fleetAnchorLines: ComponentComment[] = [];
  protected fleetAnchorLinesResolved: number = 0;

  protected gridLines: ComponentComment[] = [];
  protected gridLinesResolved: number = 0;

  protected bridles: ComponentComment[] = [];
  protected bridlesResolved: number = 0;

  protected buoys: ComponentComment[] = [];
  protected buoysResolved: number = 0;

  protected cages: ComponentComment[] = [];
  protected cagesResolved: number = 0;

  protected allCommentsAreResolved = false;

  constructor(
    private container: Container,
    private offerService: MooringOfferService,
    private mooringService: MooringService,
    private pubsub: PubSub,
    private confirm: ConfirmDialog,
    private toaster: ToastService
  ) {}

  private notifyUpdate() {
    this.pubsub.publish('entity:updated', null);
    this.getReiew(this.reviewId);
    this.getReviews();
  }

  protected statusToTranslation(status: number): string {
    return mooringReviewStatusToText[status];
  }

  @AsyncErrorHandler
  protected async updateReview() {
    if (!this.reviewId) return;
    await this.offerService.updateReview(this.reviewId, {
      MapOrientationChecked: Boolean(this.review.MapOrientationChecked),
      Comment: this.review?.Comment?.trim(),
    });
    this.notifyUpdate();
    this.toaster.showUpdated();
  }

  @AsyncErrorHandler
  protected async approveReview() {
    if (!this.reviewId) return;
    const confirmed = await this.confirm.confirmYesCancel('Godkjenn kontroll', 'Er du sikker på at du vil godkjenne kontrollen?');
    if (!confirmed) return;
    await this.offerService.completeReview(this.reviewId, {
      Comment: this.review?.Comment?.trim(),
      Approve: true,
    });
    this.toaster.showUpdated();
    this.notifyUpdate();
  }

  @AsyncErrorHandler
  protected async rejectReview() {
    if (!this.reviewId) return;
    const confirmed = await this.confirm.confirmYesCancel('Send til revisjon', 'Er du sikker på at du vil sende tilbudet tilbake til revisjon?');
    if (!confirmed) return;
    await this.offerService.completeReview(this.reviewId, {
      Comment: this.review?.Comment?.trim(),
      Approve: false,
    });
    this.toaster.showUpdated();
    this.notifyUpdate();
  }

  @AsyncErrorHandler
  protected async abortReview() {
    if (!this.reviewId) return;
    const confirmed = await this.confirm.confirmYesCancel(
      'Avbryt kontroll',
      'Er du sikker på at du vil avbryte kontrollen og gå tilbake til under arbeid?'
    );
    if (!confirmed) return;

    await this.offerService.abortReview(this.reviewId, {
      Comment: this.review?.Comment?.trim(),
    });
    this.toaster.showUpdated();
    this.notifyUpdate();
  }

  @AsyncErrorHandler
  protected async getReviews() {
    if (!this.mooringId) return;
    const { data: reviews } = await this.offerService.getOfferReviews(this.mooringId);

    this.reviews = reviews.sort(sortDateDesc('Created'));
  }

  @AsyncErrorHandler
  async getReiew(id?: number) {
    if (!id) return;
    const { data: review } = await this.offerService.getReview(id);
    const { data: comments } = await this.mooringService.getMooringComponentComments({
      mooringId: this.mooringId,
    });
    this.review = review;

    this.anchorLinesResolved = 0;
    this.fleetAnchorLinesResolved = 0;
    this.buoysResolved = 0;
    this.bridlesResolved = 0;
    this.gridLinesResolved = 0;
    this.cagesResolved = 0;
    const anchorLines: ComponentComment[] = [];
    const fleetAnchorLines: ComponentComment[] = [];
    const gridLines: ComponentComment[] = [];
    const bridles: ComponentComment[] = [];
    const buoys: ComponentComment[] = [];
    const cages: ComponentComment[] = [];

    for (const comment of comments) {
      const c = {
        Entity: comment.Entity,
        Comment: comment.Comment,
        EntityId: comment.EntityId,
        Resolved: Boolean(comment.ResolvedById),
      } satisfies ComponentComment;

      if (comment.Entity === CommentForEntity.AnchorLine || comment.Entity === CommentForEntity.AnchorLineComponent) {
        anchorLines.push(c);
        if (c.Resolved) this.anchorLinesResolved++;
      } else if (comment.Entity === CommentForEntity.GridLine || comment.Entity === CommentForEntity.GridLineComponent) {
        gridLines.push(c);
        if (c.Resolved) this.gridLinesResolved++;
      } else if (comment.Entity === CommentForEntity.Bridle || comment.Entity === CommentForEntity.BridleComponent) {
        bridles.push(c);
        if (c.Resolved) this.bridlesResolved++;
      } else if (comment.Entity === CommentForEntity.Buoy || comment.Entity === CommentForEntity.BuoyComponent) {
        buoys.push(c);
        if (c.Resolved) this.buoysResolved++;
      } else if (comment.Entity === CommentForEntity.FleetAnchorLine || comment.Entity === CommentForEntity.FleetAnchorLineComponent) {
        fleetAnchorLines.push(c);
        if (c.Resolved) this.fleetAnchorLinesResolved++;
      } else if (comment.Entity === CommentForEntity.Cage) {
        cages.push(c);
        if (c.Resolved) this.cagesResolved++;
      }
    }

    this.anchorLines = anchorLines;
    this.gridLines = gridLines;
    this.buoys = buoys;
    this.bridles = bridles;
    this.cages = cages;
    this.fleetAnchorLines = fleetAnchorLines;

    const totalComments = anchorLines.length + gridLines.length + buoys.length + bridles.length + fleetAnchorLines.length + cages.length;

    const allResolvedComments =
      this.anchorLinesResolved +
      this.gridLinesResolved +
      this.buoysResolved +
      this.bridlesResolved +
      this.fleetAnchorLinesResolved +
      this.cagesResolved;

    this.allCommentsAreResolved = totalComments - allResolvedComments == 0;
  }
}
